import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChangeLanguageResponse } from 'shared-auth';
import { User } from '../interfaces';
import { AuthService } from './auth.service';
import { TranslationCacheService } from './translation-cache.service';

/**
 * LanguageService - Centralized language management
 *
 * Purpose:
 * - Coordinate language changes across the app
 * - Clear translation cache on language change
 * - Reload current page translations
 * - Sync with backend API
 * - NO window.location.reload()!
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translateService: TranslateService,
    private cache: TranslationCacheService,
    private authService: AuthService,
    private router: Router
  ) {
    // Initialize with current language
    const lang = this.translateService.currentLang || localStorage.getItem('language') || 'en';
    this.currentLanguageSubject.next(lang);
  }

  /**
   * Change language with backend sync and cache clear
   * NO window.location.reload()!
   */
  changeLanguage(newLang: string): Observable<ChangeLanguageResponse<User>> {
    return new Observable(observer => {
      // Apply language change locally first, then sync with backend
      this.applyLanguageLocally(newLang).then(() => {
        const hasAuthenticatedSession = this.authService.isAuthenticated && !!this.authService.getAccessToken();

        // Do not call protected backend endpoint before login.
        if (!hasAuthenticatedSession) {
          observer.next({ success: true });
          observer.complete();
          return;
        }

        // Sync with backend (non-blocking - don't redirect on failure)
        this.authService.changeLanguage(newLang).subscribe({
          next: (response: ChangeLanguageResponse<User>) => {
            // Update tokens if provided
            if (response.data?.tokens) {
              localStorage.setItem('access_token', response.data.tokens.access_token);
              localStorage.setItem('refresh_token', response.data.tokens.refresh_token);
            }
            observer.next(response);
            observer.complete();
          },
          error: () => {
            // API failed (401, network error, etc.) - language already applied locally
            // Complete successfully to prevent interceptor redirect to login
            observer.next({ success: true });
            observer.complete();
          }
        });
      });
    });
  }

  /**
   * Apply language change locally (cache, TranslateService, localStorage, route reload)
   */
  private applyLanguageLocally(newLang: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cache.clear();
      this.translateService.use(newLang).subscribe({
        next: () => {
          localStorage.setItem('language', newLang);
          this.currentLanguageSubject.next(newLang);

          // Reload current route to trigger RouteTranslationResolver
          const currentUrl = this.router.url;
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigateByUrl(currentUrl).then(() => resolve());
          });
        },
        error: () => {
          // Even if translation loading fails, update localStorage
          localStorage.setItem('language', newLang);
          this.currentLanguageSubject.next(newLang);
          resolve();
        }
      });
    });
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    return ['en', 'uk', 'lv', 'ru'];
  }
}
